/**
 * Fez - Main Framework Object
 *
 * This file contains:
 * - Main Fez function (component registration and lookup)
 * - Component registry
 * - CSS utilities
 * - Pub/Sub system
 * - DOM morphing
 * - Error handling
 * - Temporary store
 *
 * For component instance methods, see instance.js
 */

// =============================================================================
// IMPORTS
// =============================================================================

import { injectCss, extractCss, cssHash } from './utils/css_inject.js';
import flattenCss from './utils/flatten_css.js';
import attachMorph from './lib/fez-morph.js';
import objectDump from './utils/dump.js';
import highlightAll from './utils/highlight_all.js';
import connect from './connect.js';
import compile from './compile.js';
import { stripGeneratedNotice } from './lib/source-parser.js';
import { WINDOW_EVENTS } from './instance.js';
import state from './lib/global-state.js';
import createTemplate from './lib/template.js';
import { subscribe, publish } from './lib/pubsub.js';
import { transitions, animateSize } from './lib/transitions.js';
import fezLocalStorage from './lib/localstorage.js';
import fezAwait from './lib/await-helper.js';
import index from './lib/index.js';
import { version } from '../../package.json';

// =============================================================================
// MAIN FEZ FUNCTION
// =============================================================================

/**
 * Main Fez function - register or find components
 *
 * @example
 * Fez('ui-foo', class { ... })  // Register component
 * Fez('ui-foo')                  // Find first instance
 * Fez(123)                       // Find by UID
 * Fez(domNode)                   // Find from DOM node
 * Fez('ui-foo', fn)              // Find all & execute callback
 *
 * @param {string|number|Node} name - Component name, UID, or DOM node
 * @param {Class|Function} [klass] - Component class or callback
 * @returns {FezBase|Array|void}
 */
const Fez = (name, klass) => {
  // Find by UID
  if (typeof name === 'number') {
    const fez = Fez.instances.get(name);
    if (fez) {
      return fez;
    }
    Fez.onError(
      'lookup',
      `Instance with UID "${name}" not found. Component may have been destroyed or never created.`,
      { uid: name },
    );
    return;
  }

  if (!name) {
    Fez.onError(
      'lookup',
      'Fez() called without arguments. Expected component name, UID, or DOM node.',
    );
    return;
  }

  // With second argument
  if (klass) {
    // an arrow function has no prototype and can never be a component class;
    // a plain function is one only when it uses this/new
    const source = typeof klass === 'function' ? klass.toString() : '';
    const isPureFn =
      typeof klass === 'function' &&
      !/^\s*class/.test(source) &&
      (!klass.prototype || !/\b(this|new)\b/.test(source));

    // Fez('name', callback) - run it for every live instance, return the instances
    if (isPureFn) {
      const list = Array.from(document.querySelectorAll(`.fez.${name}-fez`))
        .map((n) => n.fez)
        .filter(Boolean);
      list.forEach((fez) => klass(fez));
      return list;
    }

    // Fez('name', selector) - find with context
    if (typeof klass !== 'function') {
      return Fez.find(klass, name);
    }

    // Fez('name', class) - register component
    return connect(name, klass);
  }

  // Find instance by name or node
  const node = name.nodeName
    ? name.closest('.fez')
    : document.querySelector(name.includes('#') ? name : `.fez.${name}-fez`);

  if (!node) {
    Fez.onError(
      'lookup',
      `Component "${name}" not found in DOM. Ensure the component is defined and rendered.`,
      { componentName: name },
    );
    return;
  }

  if (!node.fez) {
    Fez.onError(
      'lookup',
      `DOM node "${name}" exists but has no Fez instance attached. Component may not be initialized yet.`,
      { node, tagName: name },
    );
    return;
  }

  return node.fez;
};

// =============================================================================
// COMPONENT REGISTRY
// =============================================================================

/**
 * Event names that default to `window` in `this.on('event', handler)`.
 * Anything not in this set defaults to `document`. Mutate to customize.
 */
Fez.WINDOW_EVENTS = WINDOW_EVENTS;

/** Library version, from package.json at build time */
Fez.version = version;

/** Unified component index - Fez.index['name'] = { class, meta, demo, info, source } */
Fez.index = index;

/** Counter for unique instance IDs */
Fez.instanceCount = 0;

/** Active component instances by UID */
Fez.instances = new Map();

/**
 * Find a component instance from a DOM node
 * @param {Node|string} onode - DOM node or selector
 * @param {string} [name] - Optional component name filter
 * @returns {FezBase|undefined}
 */
Fez.find = (onode, name) => {
  let node = typeof onode === 'string' ? document.body.querySelector(onode) : onode;

  // a component instance searches from its root; a jQuery object from its first node
  if (node?.root && node.UID) {
    node = node.root;
  } else if (node?.jquery) {
    node = node[0];
  }

  if (!node?.closest) {
    return;
  }

  const selector = name ? `.fez.${name}-fez` : '.fez';
  const closestNode = node.closest(selector);

  if (closestNode?.fez) {
    return closestNode.fez;
  }

  Fez.onError('find', `Node connector not found. Selector: "${selector}", node: ${onode}`, {
    original: onode,
    resolved: node,
    selector,
  });
};

// =============================================================================
// CSS UTILITIES
// =============================================================================

/**
 * Wrap CSS rules in a generated class and inject them
 * @param {string} text - CSS rules, nested or flat
 * @returns {string} Generated class name
 */
Fez.cssClass = (text) => {
  const name = cssHash(text);
  injectCss(flattenCss(`.${name} { ${text} }`));
  return name;
};

/**
 * All CSS injected so far, in injection order
 * @returns {string}
 */
Fez.extractCss = extractCss;

/**
 * Register global CSS styles
 * @param {string|Function} cssClass - CSS text or function
 * @param {Object} opts - { name, wrap }
 * @returns {string} Generated class name
 */
Fez.globalCss = (cssClass, opts = {}) => {
  if (typeof cssClass === 'function') {
    cssClass = cssClass();
  }

  let text = cssClass;

  if (opts.wrap) {
    text = `:fez { ${text} }`;
  }

  // Expand style macros here rather than at compile time - user mixins are
  // registered at runtime, so a build-time pass could never see them.
  text = Fez.cssMixin(text);

  // /g matters: a block with more than one :fez rule used to keep every
  // occurrence past the first as a literal (invalid) pseudo-class.
  if (opts.name) {
    text = text.replace(/:fez\b/g, `.fez.${opts.name}-fez`);
  }

  // Flatten here, not in the compiler: this is the one path every stylesheet
  // takes - both compile paths, component CSS() getters and public callers -
  // and user mixins have already been expanded by this point.
  return injectCss(flattenCss(text));
};

// =============================================================================
// DOM MORPHING (see lib/fez-morph.js)
// =============================================================================

attachMorph(Fez);

// =============================================================================
// PUB/SUB SYSTEM (see lib/pubsub.js)
// =============================================================================

Fez.subscribe = subscribe;
Fez.publish = publish;

// =============================================================================
// LOCAL STORAGE (see lib/localstorage.js)
// =============================================================================

Fez.localStorage = fezLocalStorage;

// =============================================================================
// ELEMENT TRANSITIONS - fez:in / fez:out (see lib/transitions.js)
// =============================================================================

Fez.transitions = transitions;

/**
 * Animate an element's box when its content changes - what fez:animate="height"
 * does, for plain DOM outside templates:
 *   Fez.animateSize(node, 'height, duration=250, easing=cubicOut')
 */
Fez.animateSize = animateSize;

// =============================================================================
// ASYNC AWAIT HELPER (see lib/await-helper.js)
// =============================================================================

Fez.fezAwait = fezAwait;

// =============================================================================
// ERROR HANDLING & LOGGING
// =============================================================================

Fez.consoleError = (text, show) => {
  text = `Fez: ${text}`;
  console.error(text);
  if (show) {
    return `<span style="border: 1px solid red; font-size: 14px; padding: 3px 7px; background: #fee; border-radius: 4px;">${text}</span>`;
  }
};

Fez.consoleLog = (text) => {
  if (Fez.LOG) {
    console.log(`Fez: ${String(text).substring(0, 180)}`);
  }
};

/**
 * Enhanced error handler with component context
 * @param {string} kind - Error category (e.g., 'template', 'lifecycle', 'morph')
 * @param {string|Error} message - Error message or Error object
 * @param {Object} [context] - Additional context (component name, props, etc.)
 * @returns {string} Formatted error message
 */
Fez.onError = (kind, message, context) => {
  // Extract component name from context or message
  let componentName = context?.componentName || context?.name;

  // Try to extract component name from message if not in context
  if (!componentName && typeof message === 'string') {
    const match = message.match(/<([^>]+)>/);
    if (match) {
      componentName = match[1];
    }
  }

  // Format the error message with component context
  const prefix = componentName ? ` [${componentName}]` : '';
  const errorMsg = typeof message === 'string' ? message : message?.message || String(message);
  const fullMessage = `Fez ${kind}:${prefix} ${errorMsg}`;

  // Log with context if available
  if (context && Fez.LOG) {
    console.error(fullMessage, context);
  } else {
    console.error(fullMessage);
  }

  // The stack of a thrown error is always worth showing; the rest of the context only with LOG
  const error = message instanceof Error ? message : context?.error;
  if (error instanceof Error && error.stack) {
    console.error(error.stack);
  }

  return fullMessage;
};

// =============================================================================
// LOAD UTILITIES & EXPORTS
// =============================================================================

import addUtilities from './lib/utility.js';
import cssMixin from './utils/css_mixin.js';

addUtilities(Fez);
cssMixin(Fez);

Fez.compile = compile;
Fez.stripGeneratedNotice = stripGeneratedNotice;
Fez.createTemplate = createTemplate;
Fez.state = state;
Fez.dump = objectDump;
Fez.highlightAll = highlightAll;

Fez.onReady(() => Fez.consoleLog('Fez.LOG === true, logging enabled.'));

export default Fez;
